import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { useState, useEffect } from 'react';

interface AnonymousAvatarProps {
    className?: string;
    size?: 'sm' | 'md' | 'lg';
}

/**
 * AnonymousAvatar component for displaying the anonymous user avatar
 * This is a non-interactive avatar used for all anonymous yaps
 */
export function AnonymousAvatar({ className = '', size = 'md' }: AnonymousAvatarProps) {
    // Initialize with the actual dark mode state immediately
    const [isDark, setIsDark] = useState(() => {
        if (typeof document !== 'undefined') {
            return document.documentElement.classList.contains('dark');
        }
        return false;
    });

    const sizeClasses = {
        sm: 'w-8 h-8',
        md: 'w-10 h-10',
        lg: 'w-12 h-12',
    };

    useEffect(() => {
        // Check initial dark mode
        const checkDarkMode = () => {
            const darkMode = document.documentElement.classList.contains('dark');
            console.log('[AnonymousAvatar] Dark mode detected:', darkMode);
            setIsDark(darkMode);
        };

        checkDarkMode();

        // Watch for changes to the dark class
        const observer = new MutationObserver(checkDarkMode);
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['class']
        });

        return () => observer.disconnect();
    }, []);

    const pathColor = isDark ? '#4478ea' : '#F39C12';
    const circleColor = isDark ? '#2d1ef1' : '#E74C3C';

    console.log('[AnonymousAvatar] Rendering with isDark:', isDark, 'pathColor:', pathColor, 'circleColor:', circleColor);

    return (
        <Avatar className={`${sizeClasses[size]} ${className} cursor-default`}>
            <AvatarImage
                src={isDark ? '/anonymous-avatar-blue.png' : '/anonymous-avatar-orange.png'}
                alt="Anonymous"
            />
            <AvatarFallback className="bg-linear-to-br from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#2d1ef1] text-white">
                <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    className="w-full h-full"
                >
                    <circle
                        cx="12"
                        cy="8"
                        r="4"
                        fill={isDark ? '#4478ea' : '#F39C12'}
                        style={{ fill: isDark ? '#4478ea' : '#F39C12' }}
                    />
                    <path
                        d="M4 20c0-4.418 3.582-8 8-8s8 3.582 8 8"
                        fill={isDark ? '#2d1ef1' : '#E74C3C'}
                        style={{ fill: isDark ? '#2d1ef1' : '#E74C3C' }}
                    />
                </svg>
            </AvatarFallback>
        </Avatar>
    );
}

// Constants for anonymous user display
export const ANONYMOUS_USER = {
    username: '@anonymous',
    displayName: 'Anonymous',
    avatar: '/anonymous-avatar.png',
} as const;

